import { useSnackbar } from 'notistack'
import React, { useEffect, useState } from 'react'
import ReactGA from 'react-ga'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { useAccount } from 'wagmi'
import { useAuthAPI } from '../../../__legacy__/api/v2/AuthAPI'
import { useAuth } from '../../hooks/useAuth'
import { useUpdateUserTwitterHandle } from '../../queries/users'
import Button from '../core/Button/Button'
import { FieldErrorLabel } from '../core/FieldErrorLabel'
import { FieldLabel } from '../core/FieldLabel'
import { Flex } from '../core/Flex'
import Spacer from '../core/Spacer/Spacer'
import Text from '../core/Text/Text'
import { TextInput } from '../core/TextInput'
import { EditProfileFlowPosition } from '../EditProfileFlowModal/EditProfileFlowModal'
import { Spinner } from '../platform/explore/common/utils'
import { TwitterLogo } from '../platform/icons'
import { AuthFlowPosition } from './AuthFlowModal'

export type VerifyOnTwitterForm = {
  tweetUrl: string
}

export const VerifyOnTwitter = ({
  setFlowPosition,
  flowType,
  onSuccess
}: {
  setFlowPosition: React.Dispatch<React.SetStateAction<AuthFlowPosition | EditProfileFlowPosition>>
  flowType?: 'editProfile' | 'auth'
  onSuccess?: () => void
}) => {
  const { getAuthInfo } = useAuthAPI()
  const { user, getAndSetUser, setUser } = useAuth()

  const updateUserTwitterHandle = useUpdateUserTwitterHandle(user.wallet_address)
  const { address: walletAddress } = useAccount()
  const { enqueueSnackbar } = useSnackbar()
  const [hashedUserIdentifier, setHashedUserIdentifier] = useState<string | null>(null)

  const getAndSetHashedUserIdentifier = async () => {
    const { identifier } = await getAuthInfo(walletAddress)
    setHashedUserIdentifier(identifier)
  }

  const {
    control,
    handleSubmit,
    formState: { errors }
  } = useForm<VerifyOnTwitterForm>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: {
      tweetUrl: ''
    }
  })

  useEffect(() => {
    if (user) getAndSetHashedUserIdentifier()
  }, [user])

  const twitterVerifyUrl =
    'https://twitter.com/intent/tweet?text=Verifying%20my%20Arpeggi%20account%20%40arpeggi_labs%0A%0Ahttps://www.arpeggi.io%0A%0A'

  const onClickTweet = () => {
    window.open(`${twitterVerifyUrl}${hashedUserIdentifier}`, '_blank')
  }

  const onClickSubmit: SubmitHandler<VerifyOnTwitterForm> = async () => {
    ReactGA.event({
      category: 'User',
      action: 'Clicked Verify Twitter'
    })

    enqueueSnackbar('Twitter verified!', {
      variant: 'modalSuccess'
    })

    if (flowType === 'editProfile') {
      setFlowPosition('editProfile')
    } else {
      if (onSuccess) onSuccess()
      setFlowPosition('close')
    }
  }

  const onClickBack = () => setFlowPosition('editProfile')

  return (
    <Flex direction={'column'} css={{ justifyContent: 'center', alignItems: 'center' }}>
      <TwitterLogo width={'54px'} height={'54px'} />

      <Spacer type={'vertical_5'} />

      <Text css={{ textAlign: 'center' }} size={'32'} type={'primary'}>
        Verify on Twitter
      </Text>

      <Spacer type={'vertical_5'} />

      <Flex direction="column">
        <FieldLabel>Step 1</FieldLabel>
        <Text size={'14'} type={'secondary'}>
          Post a public tweet containing your identifier.
        </Text>

        <Spacer type={'vertical_2'} />

        <Flex css={{ justifyContent: 'flex-start' }}>
          <Button
            type="dark"
            big={true}
            rounded={true}
            css={{ justifyContent: 'flex-start', width: 'auto', backgroundColor: '#1D9BF0' }}
            onClick={onClickTweet}
          >
            <Text size={'14'} type={'primary_inverted'}>
              Tweet
            </Text>
          </Button>
        </Flex>

        <Spacer type={'vertical_2'} />

        <form onSubmit={handleSubmit(onClickSubmit)}>
          <Flex
            direction={'column'}
            width={'full'}
            css={{
              marginRight: '16px'
            }}
          >
            <FieldLabel htmlFor="displayName">Step 2</FieldLabel>
            <Text size={'14'} type={'secondary'}>
              Verify your tweet*
            </Text>
            <Spacer type={'vertical_1'} />
            <Controller
              name="tweetUrl"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: 'Required'
                },
                validate: async (value) => {
                  try {
                    await updateUserTwitterHandle.mutateAsync(value, {
                      onSuccess: async (user) => {
                        setUser(user)
                      }
                    })
                    return true
                  } catch {
                    return 'Invalid tweet url. Make sure tweet visibility is set to "everyone".'
                  }
                }
              }}
              render={({ field }) => (
                <TextInput id="tweetUrl" placeholder="Your tweet url" {...field} />
              )}
            />
            <FieldErrorLabel>{errors.tweetUrl?.message}</FieldErrorLabel>
          </Flex>
        </form>
      </Flex>

      <Spacer type={'vertical_4'} />

      <Flex direction="column" width={'full'}>
        <Button
          type={'dark'}
          big={true}
          rounded={true}
          disabled={updateUserTwitterHandle.isLoading}
          onClick={handleSubmit(onClickSubmit)}
        >
          {updateUserTwitterHandle.isLoading ? (
            <Spinner />
          ) : (
            <Text size={'14'} type={'primary_inverted'}>
              Verify
            </Text>
          )}
        </Button>
        {flowType === 'editProfile' ? (
          <>
            <Spacer type={'vertical_1'} />
            <Button
              type={'secondary'}
              big={true}
              rounded={true}
              disabled={updateUserTwitterHandle.isLoading}
              onClick={onClickBack}
            >
              <Text size={'14'} type={'primary'}>
                Back
              </Text>
            </Button>
          </>
        ) : null}
      </Flex>
    </Flex>
  )
}
